import { useCallback, useEffect, useState } from "react";
import AdminBoxConent from "../../components/admin/AdminBoxContent";
import User from "../../models/user";
import userManager from "../../models/userManager";
import userSyncService from "../../services/userSyncService";
import DropdownOption from "../../components/common/DropdownOption";
import Select from "react-select";
import UserTag from "../../models/userTag";
import ModalWindow from "../../components/common/overlays/ModalWindow";
import AddRemoveTags from "../../components/admin/user-tagging/AddRemoveTags";
import AddRemoveTagsData from "../../components/admin/user-tagging/AddRemoveTagsData";
import promiseHelper from "../../util/promiseHelper";
import SyncStatus from "../../models/syncStatus";
import UserSyncStatus from "../../models/userSyncStatus";
import Moment from "react-moment";
import dateTimeHelper from "../../util/dateTimeHelper";
import SyncAction from "../../models/syncAction";
import stringHelper from "../../util/stringHelper";
import userSyncManager from "../../models/userSyncManager";
import OrganizationStat from "../../models/organizationStat";
import organizationService from "../../services/organizationService";

const UserTagging: React.FC<{}> = () => {
  const [isBusy, setIsBusy] = useState<boolean>(false);
  //const [errorMessage, setErrorMessage] = useState<string>();

  const [users, setUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);

  const [organizationStats, setOrganizationStats] = useState<
    OrganizationStat[]
  >([]);

  const [userTags, setUserTags] = useState<UserTag[]>([]);

  const [searchText, setSearchText] = useState<string>("");

  const [isFilterByNoTags, setIsFilterByNoTags] = useState<boolean>(false);
  const [isFilterByWithTags, setIsFilterByWithTags] = useState<boolean>(false);

  const [isFilterByIsPaidSubscription, setIsFilterByIsPaidSubscription] =
    useState<boolean>(false);
  const [
    isFilterByIs90DayTrialSubscription,
    setIsFilterByIs90DayTrialSubscription,
  ] = useState<boolean>(false);
  const [
    isFilterByIs1YearTrialSubscription,
    setIsFilterByIs1YearTrialSubscription,
  ] = useState<boolean>(false);

  const [isFilterByIsTrialSubscription, setIsFilterByIsTrialSubscription] =
    useState<boolean>(false);

  const [isFilterByIsExpiredSubscription, setIsFilterByIsExpiredSubscription] =
    useState<boolean>(false);

  const [organizationNameOptions, setOrganizationNameOptions] = useState<
    DropdownOption<string>[]
  >([]);
  const [selectedOrganizationNameOptions, setSelectedOrganizationNameOptions] =
    useState<DropdownOption<string>[]>([]);

  const [userTagOptions, setUserTagOptions] = useState<
    DropdownOption<UserTag>[]
  >([]);
  const [selectedUserTagOptions, setSelectedUserTagOptions] = useState<
    DropdownOption<UserTag>[]
  >([]);

  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);
  const [selectedCount, setSelectedCount] = useState<number>(0);

  const [showTagUsersAddRemoveTags, setShowTagUsersAddRemoveTags] =
    useState<boolean>(false);

  const [addRemoveTagsData, setAddRemoveTagsData] = useState<AddRemoveTagsData>(
    new AddRemoveTagsData(),
  );

  const [userSyncStatus, setUserSyncStatus] = useState<UserSyncStatus[]>();
  const [lastSyncDate, setLastSyncDate] = useState<Date>();

  const [showConfirmSyncUsers, setShowConfirmSyncUsers] =
    useState<Boolean>(false);

  const init = async () => {
    const { data: users } = await userSyncService.getUsersWithTags();
    setAllUsers(users);
    setUsers(users);

    const { data: organizationStats } =
      await organizationService.getOrganizationStats();
    setOrganizationStats(organizationStats);

    let userTags: UserTag[] = [];

    for (const user of users) {
      for (const userTag of user.userTags) {
        if (!userTags.find((ut) => ut.tagName === userTag.tagName)) {
          userTags.push(userTag);
        }
      }
    }

    const organizationOptions: DropdownOption<string>[] = organizationStats.map(
      (organizationStat) => {
        return {
          value: organizationStat.organizationName,
          label: organizationStat.organizationName,
        };
      },
    );
    setOrganizationNameOptions(organizationOptions);

    userTags = userTags.sort((a, b) => {
      if (a.tagName < b.tagName) {
        return -1;
      }

      if (a.tagName > b.tagName) {
        return 1;
      }

      return 0;
    });

    const userTagOptions: DropdownOption<UserTag>[] = userTags.map((ut) => {
      return { value: ut, label: ut.tagName };
    });
    setUserTagOptions(userTagOptions);

    const { data: allUserTags } = await userSyncService.getUserTags();
    setUserTags(allUserTags);
  };

  const [isSyncInProgress, setIsSyncInProgress] = useState<boolean>(false);

  const updateLastSyncDate = async () => {
    const { data: syncStatus } = await userSyncService.getUserSyncStatus();
    const status = syncStatus.find(
      (s) => s.syncAction === SyncAction.SyncUsers,
    );
    setLastSyncDate(status?.endDate);
  };

  // on page load
  useEffect(() => {
    if (isSyncInProgress) {
      return;
    }

    const initialize = async () => {
      let { data: userSyncStatus } = await userSyncService.getUserSyncStatus();

      if (userSyncManager.checkIsSyncInProgress(userSyncStatus)) {
        setIsSyncInProgress(true);
        return;
      }

      if (userSyncManager.checkIsSyncError(userSyncStatus)) {
        setUserSyncStatus(userSyncStatus);
        setIsBusy(true);
        return;
      }

      await updateLastSyncDate();

      await init();
    };

    initialize();
  }, [isSyncInProgress]);

  // on page load (and) when sync in progress
  useEffect(() => {
    if (!isSyncInProgress) {
      return;
    }

    let isDisposed = false;

    const checkAsync = async () => {
      let { data: userSyncStatus } = await userSyncService.getUserSyncStatus();

      if (userSyncManager.checkIsSyncInProgress(userSyncStatus)) {
        setIsBusy(true);
        setUserSyncStatus(userSyncStatus);
      }

      while (
        userSyncManager.checkIsSyncInProgress(userSyncStatus) &&
        !isDisposed
      ) {
        await promiseHelper.delay(1000);
        const data = await userSyncService.getUserSyncStatus();
        userSyncStatus = data.data;
        setUserSyncStatus(userSyncStatus);
      }

      if (!userSyncManager.checkIsSyncError(userSyncStatus)) {
        setIsBusy(false);
      }

      setIsSyncInProgress(false);
    };

    checkAsync();

    return () => {
      isDisposed = true;
    };
  }, [isSyncInProgress]);

  useEffect(() => {
    const clearSelected = () => {
      setIsSelectAll(false);
      setSelectedCount(0);

      for (const user of allUsers) {
        user.isSelected = false;
      }

      // const updatedUsers = [...allUsers];
      // setUsers(updatedUsers);
    };

    clearSelected();

    const searchToken = searchText.toLowerCase();

    let filteredUsers = allUsers.filter(
      (u) =>
        userManager
          .getUserOrganizationNames(u)
          .toLowerCase()
          .includes(searchToken) ||
        u.email.toLowerCase().includes(searchToken) ||
        u.firstName.toLowerCase().includes(searchToken) ||
        u.lastName.toLowerCase().includes(searchToken) ||
        u.userTags.filter((ut) =>
          ut.tagName.toLowerCase().includes(searchToken),
        ).length > 0,
    );

    if (isFilterByNoTags) {
      filteredUsers = filteredUsers.filter((u) => u.userTags.length === 0);
    }

    if (isFilterByWithTags) {
      filteredUsers = filteredUsers.filter((u) => u.userTags.length > 0);
    }

    const isAnyOrganization = (user: User, action: any) => {
      const userOrganizationNames =
        userManager.getUserOrganizationNameList(user);

      let isAnyOrganization = false;

      for (let userOrganizationName of userOrganizationNames) {
        const orgStat = organizationStats.find(
          (st) => st.organizationName === userOrganizationName,
        );

        if (orgStat) {
          isAnyOrganization = isAnyOrganization || action(orgStat);
        }
      }

      return isAnyOrganization;
    };

    if (isFilterByIsPaidSubscription) {
      filteredUsers = filteredUsers.filter((u) =>
        isAnyOrganization(u, (orgStat: OrganizationStat) => {
          return orgStat.isPaidSubscription;
        }),
      );
    }

    if (isFilterByIs90DayTrialSubscription) {
      filteredUsers = filteredUsers.filter((u) =>
        isAnyOrganization(
          u,
          (orgStat: OrganizationStat) => orgStat.is90DayTrialSubscription,
        ),
      );
    }

    if (isFilterByIs1YearTrialSubscription) {
      filteredUsers = filteredUsers.filter((u) =>
        isAnyOrganization(
          u,
          (orgStat: OrganizationStat) => orgStat.is1YearTrialSubscription,
        ),
      );
    }

    if (isFilterByIsTrialSubscription) {
      filteredUsers = filteredUsers.filter((u) =>
        isAnyOrganization(
          u,
          (orgStat: OrganizationStat) =>
            orgStat.is90DayTrialSubscription ||
            orgStat.is1YearTrialSubscription,
        ),
      );
    }

    if (isFilterByIsExpiredSubscription) {
      filteredUsers = filteredUsers.filter((u) =>
        isAnyOrganization(u, (orgStat: OrganizationStat) => orgStat.isExpired),
      );
    }

    if (selectedOrganizationNameOptions.length > 0) {
      filteredUsers = filteredUsers.filter(
        (u) =>
          selectedOrganizationNameOptions.filter((option) =>
            userManager
              .getUserOrganizationNames(u)
              .includes(option.value ?? ""),
          ).length > 0,
      );
    }

    if (selectedUserTagOptions.length > 0) {
      filteredUsers = filteredUsers.filter(
        (u) =>
          selectedUserTagOptions.filter(
            (option) =>
              u.userTags.filter((ut) => ut.tagName === option.value?.tagName)
                .length > 0,
          ).length > 0,
      );
    }

    setUsers(filteredUsers);
  }, [
    allUsers,
    organizationStats,
    searchText,
    isFilterByNoTags,
    isFilterByWithTags,
    isFilterByIsPaidSubscription,
    isFilterByIs90DayTrialSubscription,
    isFilterByIs1YearTrialSubscription,
    isFilterByIsTrialSubscription,
    isFilterByIsExpiredSubscription,
    selectedOrganizationNameOptions,
    selectedUserTagOptions,
  ]);

  const onSearchTextChange = (event: any) => {
    const value = event.target.value.toLowerCase();
    setSearchText(value);
  };

  const setIsFilterByNoTagsCheckboxChecked = (event: any) => {
    const checked: boolean = event.target.checked;
    setIsFilterByNoTags(checked);

    setIsFilterByWithTags(false);
  };

  const setIsFilterByWithTagsCheckboxChecked = (event: any) => {
    const checked: boolean = event.target.checked;
    setIsFilterByWithTags(checked);

    setIsFilterByNoTags(false);
  };

  const setIsFilterByIsPaidSubscriptionCheckboxChecked = (event: any) => {
    const checked: boolean = event.target.checked;
    setIsFilterByIsPaidSubscription(checked);

    setIsFilterByIs90DayTrialSubscription(false);
    setIsFilterByIs1YearTrialSubscription(false);
    setIsFilterByIsTrialSubscription(false);
  };

  const setIsFilterByIs90DayTrialSubscriptionCheckboxChecked = (event: any) => {
    const checked: boolean = event.target.checked;
    setIsFilterByIs90DayTrialSubscription(checked);

    setIsFilterByIsPaidSubscription(false);
    setIsFilterByIs1YearTrialSubscription(false);
    setIsFilterByIsTrialSubscription(false);
  };

  const setIsFilterByIs1YearTrialSubscriptionCheckboxChecked = (event: any) => {
    const checked: boolean = event.target.checked;
    setIsFilterByIs1YearTrialSubscription(checked);

    setIsFilterByIsPaidSubscription(false);
    setIsFilterByIs90DayTrialSubscription(false);
    setIsFilterByIsTrialSubscription(false);
  };

  const setIsFilterByIsTrialSubscriptionCheckboxChecked = (event: any) => {
    const checked: boolean = event.target.checked;
    setIsFilterByIsTrialSubscription(checked);

    setIsFilterByIsPaidSubscription(false);
    setIsFilterByIs90DayTrialSubscription(false);
    setIsFilterByIs1YearTrialSubscription(false);
  };

  const setIsFilterByIsExpiredSubscriptionCheckboxChecked = (event: any) => {
    const checked: boolean = event.target.checked;
    setIsFilterByIsExpiredSubscription(checked);
  };

  const onSelectedOrganizationOptionsChanged = async (options: []) => {
    setSelectedOrganizationNameOptions(options);
  };

  const onSelectedUserTagOptionsChanged = async (options: []) => {
    setSelectedUserTagOptions(options);
  };

  const onSelectUserChanged = (event: any, user: User) => {
    const checked = event.target.checked;
    user.isSelected = checked;

    updateUsers();
  };

  const onRowClick = (event: any, user: User) => {
    user.isSelected = !user.isSelected;
    updateUsers();
  };

  const onIsSelectAllChanged = (event: any) => {
    const checked = event.target.checked;
    setIsSelectAll(checked);

    for (const user of users) {
      user.isSelected = checked;
    }

    updateUsers();
  };

  const updateUsers = () => {
    const updatedUsers = [...users];
    setUsers(updatedUsers);

    const selectedCount = getSelectedUsers().length;
    setSelectedCount(selectedCount);
  };

  const getSelectedUsers = (): User[] => {
    const selectedUsers = users.filter((u) => u.isSelected);
    return selectedUsers;
  };

  const onTagUsersAddRemoveTagsClick = () => {
    setShowTagUsersAddRemoveTags(true);
  };

  const onCancelTagUsersAddRemoveTags = () => {
    setShowTagUsersAddRemoveTags(false);
  };

  const onOkTagUsersAddRemoveTags = async () => {
    setShowTagUsersAddRemoveTags(false);

    const selectedUsers = getSelectedUsers();
    const emails = selectedUsers.map((u) => u.email);
    const tags = addRemoveTagsData.userTags.map((t) => t.tagName);

    await userSyncService.scheduleJobTagUsers(
      addRemoveTagsData.tagAction,
      tags,
      emails,
    );

    setIsSyncInProgress(true);
  };

  const onAddRemoveTagsChanged = useCallback(
    (addRemoveTagsData: AddRemoveTagsData) => {
      setAddRemoveTagsData(addRemoveTagsData);
    },
    [],
  );

  // --------- UserSync

  const onConfirmSyncUsers = () => {
    setShowConfirmSyncUsers(true);
  };

  const onCancelSyncUsers = () => {
    setShowConfirmSyncUsers(false);
  };

  const onOkSyncUsers = async () => {
    setShowConfirmSyncUsers(false);

    await userSyncService.scheduleJobSyncUsers();

    setIsSyncInProgress(true);
  };

  /////////////////////

  return (
    <AdminBoxConent
      title="User Tagging"
      hasCount={isBusy ? false : true}
      count={users.length}
      countType="users"
      hasAddNew={true}
      isAddNewEnabled={!isSyncInProgress}
      addNewTitle="Sync Users"
      onAddNew={onConfirmSyncUsers}
      beforeAddNewContent={
        <div className="me-2" style={{ fontSize: "12px", marginTop: "-4px" }}>
          Last Sync:{" "}
          {lastSyncDate && (
            <Moment format={dateTimeHelper.dateTimeFormat}>
              {lastSyncDate}
            </Moment>
          )}
          {!lastSyncDate && "-"}
        </div>
      }
      hasCurrentOrganizationDropdown={false}
    >
      {isBusy && (
        <div>
          Please wait... Sync in Progress...{" "}
          <span>
            {
              userSyncStatus?.find(
                (s) => s.syncStatus === SyncStatus.InProgress,
              )?.statusMessage
            }
          </span>
          {userSyncStatus?.find((s) => s.syncStatus === SyncStatus.Error) && (
            <span style={{ color: "red" }}>
              <b>Error:</b>{" "}
              <span
                dangerouslySetInnerHTML={{
                  __html: stringHelper.replaceNewLineWithHtmlBreaks(
                    userSyncStatus?.find(
                      (s) => s.syncStatus === SyncStatus.Error,
                    )?.statusMessage,
                  ),
                }}
              ></span>
            </span>
          )}
        </div>
      )}

      {!isBusy && (
        <>
          <table style={{ borderCollapse: "separate", borderSpacing: "10px" }}>
            <tbody>
              <tr>
                <td style={{ textWrap: "nowrap" }}>Filter by Organizations</td>
                <td style={{ width: "100%" }}>
                  <Select
                    options={organizationNameOptions}
                    value={selectedOrganizationNameOptions}
                    isMulti
                    onChange={(options: any) => {
                      onSelectedOrganizationOptionsChanged(options);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <td style={{ textWrap: "nowrap" }}>Filter by User Tags</td>
                <td style={{ width: "100%" }}>
                  <Select
                    options={userTagOptions}
                    value={selectedUserTagOptions}
                    isMulti
                    onChange={(options: any) => {
                      onSelectedUserTagOptionsChanged(options);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <td style={{ textWrap: "nowrap" }}>Filter by</td>
                <td
                  style={{ width: "100%" }}
                  className="d-flex align-items-center"
                >
                  <div className="btn-group">
                    <input
                      type="checkbox"
                      checked={isFilterByNoTags}
                      onChange={(e) => setIsFilterByNoTagsCheckboxChecked(e)}
                      className="btn-check"
                      id="btncheck_isFilterByNoTags"
                      autoComplete="off"
                    />
                    <label
                      className="btn btn-outline-primary btn-sm"
                      htmlFor="btncheck_isFilterByNoTags"
                    >
                      No Tags
                    </label>
                    <input
                      type="checkbox"
                      checked={isFilterByWithTags}
                      onChange={(e) => setIsFilterByWithTagsCheckboxChecked(e)}
                      className="btn-check"
                      id="btncheck_isFilterByWithTags"
                      autoComplete="off"
                    />
                    <label
                      className="btn btn-outline-primary btn-sm"
                      htmlFor="btncheck_isFilterByWithTags"
                    >
                      With Tags
                    </label>
                  </div>
                  <div className="ms-5 me-3">Subscription</div>
                  <div className="btn-group">
                    <input
                      type="checkbox"
                      checked={isFilterByIsPaidSubscription}
                      onChange={(e) =>
                        setIsFilterByIsPaidSubscriptionCheckboxChecked(e)
                      }
                      className="btn-check"
                      id="btncheck_isFilterByIsPaidSubscription"
                      autoComplete="off"
                    />
                    <label
                      className="btn btn-outline-primary btn-sm"
                      htmlFor="btncheck_isFilterByIsPaidSubscription"
                    >
                      Is Paid
                    </label>
                    <input
                      type="checkbox"
                      checked={isFilterByIs90DayTrialSubscription}
                      onChange={(e) =>
                        setIsFilterByIs90DayTrialSubscriptionCheckboxChecked(e)
                      }
                      className="btn-check"
                      id="btncheck_isFilterByIs90DayTrialSubscription"
                      autoComplete="off"
                    />
                    <label
                      className="btn btn-outline-primary btn-sm"
                      htmlFor="btncheck_isFilterByIs90DayTrialSubscription"
                    >
                      Is 90 Day Trial
                    </label>
                    <input
                      type="checkbox"
                      checked={isFilterByIs1YearTrialSubscription}
                      onChange={(e) =>
                        setIsFilterByIs1YearTrialSubscriptionCheckboxChecked(e)
                      }
                      className="btn-check"
                      id="btncheck_isFilterByIs1YearTrialSubscription"
                      autoComplete="off"
                    />
                    <label
                      className="btn btn-outline-primary btn-sm"
                      htmlFor="btncheck_isFilterByIs1YearTrialSubscription"
                    >
                      Is 1 Year Trial
                    </label>
                    <input
                      type="checkbox"
                      checked={isFilterByIsTrialSubscription}
                      onChange={(e) =>
                        setIsFilterByIsTrialSubscriptionCheckboxChecked(e)
                      }
                      className="btn-check"
                      id="btncheck_isFilterByIsTrialSubscription"
                      autoComplete="off"
                    />
                    <label
                      className="btn btn-outline-primary btn-sm"
                      htmlFor="btncheck_isFilterByIsTrialSubscription"
                    >
                      Is 90 Day or 1 Year Trial
                    </label>
                    <input
                      type="checkbox"
                      checked={isFilterByIsExpiredSubscription}
                      onChange={(e) =>
                        setIsFilterByIsExpiredSubscriptionCheckboxChecked(e)
                      }
                      className="btn-check"
                      id="btncheck_isFilterByIsExpiredSubscription"
                      autoComplete="off"
                    />
                    <label
                      className="btn btn-outline-primary btn-sm"
                      htmlFor="btncheck_isFilterByIsExpiredSubscription"
                    >
                      Is Expired
                    </label>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div className="mb-3">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={searchText}
                onChange={onSearchTextChange}
              />
              <button type="submit" className="btn btn-primary">
                Search
              </button>
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <span>
              <b>{selectedCount}</b> selected
            </span>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={selectedCount === 0}
              onClick={() => onTagUsersAddRemoveTagsClick()}
            >
              Tag Users
            </button>
          </div>

          <table className="table table-hover">
            <thead>
              <tr>
                <th scope="col" style={{ width: "10px" }}>
                  <input
                    className="form-check-input form-switch-right-text"
                    type="checkbox"
                    checked={isSelectAll}
                    onChange={(event) => onIsSelectAllChanged(event)}
                  />
                </th>
                <th scope="col">Organization</th>
                <th scope="col">Email</th>
                <th scope="col">First Name</th>
                <th scope="col">Last Name</th>
                <th scope="col">User Tags</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user: User, indexUser) => (
                <tr
                  key={indexUser.toString()}
                  onClick={(event) => onRowClick(event, user)}
                >
                  <td>
                    <input
                      className="form-check-input form-switch-right-text"
                      type="checkbox"
                      checked={user.isSelected ?? false}
                      onChange={(event) => onSelectUserChanged(event, user)}
                    />
                  </td>
                  <td>{userManager.getUserOrganizationNames(user)}</td>
                  <td>{user.email}</td>
                  <td>{user.firstName}</td>
                  <td>{user.lastName}</td>
                  <td>
                    {user.userTags.map((userTag, indexTag) => (
                      <span
                        key={`${indexUser}_${indexTag}`}
                        // style={{ textWrap: "nowrap" }}
                        className="user-tag me-2"
                      >
                        {userTag.tagName}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {showTagUsersAddRemoveTags && (
            <ModalWindow
              title="Add or Remove Tags"
              onOk={onOkTagUsersAddRemoveTags}
              isOkEnabled={addRemoveTagsData.userTags.length > 0}
              onCancel={onCancelTagUsersAddRemoveTags}
              okButtonTitle="Apply"
            >
              <AddRemoveTags
                userTags={userTags}
                selectedUserCount={selectedCount}
                onAddRemoveTagsChanged={onAddRemoveTagsChanged}
              />
            </ModalWindow>
          )}
        </>
      )}

      {showConfirmSyncUsers && (
        <ModalWindow
          title="Sync Users"
          onOk={onOkSyncUsers}
          onCancel={onCancelSyncUsers}
        >
          <div>
            Are you sure you want to start the Sync Users process to associate
            Users the Tags from Mailchimp? <br />
            <br />
            This will take a while and User Tagging will not be available during
            sync.
          </div>
        </ModalWindow>
      )}
    </AdminBoxConent>
  );
};

export default UserTagging;
